import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PortalModule, Role } from '@ciimar/shared';
import { useAuth } from '../auth/AuthContext';
import { useAccess } from '../auth/AccessContext';
import { useBranding } from '../branding/BrandingContext';
import { Icon } from './primitives';
import { initialsOf } from '../components/PersonCell';
import { MODULE_ICONS, MODULE_KEY } from './modules';
import { TabBar } from '../components/TabBar';
import { CommandPalette, type Command } from '../components/CommandPalette';
import { NotificationsBell } from './NotificationsBell';
import { ImpersonationBanner } from './ImpersonationBanner';
import { useI18n, type Translate } from '../lib/i18n';

/**
 * The unified application shell, built on the ERP token layer so the whole
 * frame matches the "ERP institucional" design: a grouped sidebar with a
 * squared brand mark, and a translucent glass header carrying the breadcrumb +
 * page title and the user actions. The browser-style TabBar sits between the
 * header and the content. Navigation is built from the user's module access
 * (`/permissions/modules/me`): everyone sees "Meu Perfil"; granted modules add
 * their entries; admins see everything.
 */

interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
  /** Sub-options; when present the item is an expand/collapse parent. */
  children?: NavItem[];
}
interface NavGroup {
  /** Language-independent id; used as the collapse-state key and React key. */
  id: string;
  label: string;
  items: NavItem[];
  /** The top essentials block: no header, never collapses, always visible. */
  essential?: boolean;
}

function isActive(pathname: string, item: NavItem): boolean {
  return item.end
    ? pathname === item.to
    : pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/** True when the item (or any of its children) matches the current route. */
function isBranchActive(pathname: string, item: NavItem): boolean {
  if (item.children?.some((c) => isActive(pathname, c))) return true;
  return isActive(pathname, item);
}

function useTheme(): [string, () => void] {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    try {
      const t = localStorage.getItem('ciimar-theme');
      if (t === 'dark' || t === 'light') return t;
    } catch {
      /* storage may be unavailable */
    }
    return 'light';
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('ciimar-theme', theme);
    } catch {
      /* ignore */
    }
  }, [theme]);
  return [theme, () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))];
}

function mod(module: PortalModule, to: string, t: Translate): NavItem {
  return { to, label: t(`modules.${MODULE_KEY[module]}`), icon: MODULE_ICONS[module] };
}

function buildGroups(opts: {
  has: (m: PortalModule) => boolean;
  isManager: boolean;
  isResearcher: boolean;
  isAdminIt: boolean;
  t: Translate;
}): NavGroup[] {
  const { has, isManager, isResearcher, isAdminIt, t } = opts;
  const groups: NavGroup[] = [];

  // Essentials: Painel + Meu Perfil, always visible at the top (no header).
  const essentials: NavItem[] = [];
  if (has(PortalModule.Dashboard) || isManager || isResearcher) {
    essentials.push({ to: '/', label: t('nav.painel'), icon: 'home', end: true });
  }
  essentials.push({ to: '/me', label: t('nav.meuPerfil'), icon: 'user', end: true });
  groups.push({
    id: 'essentials',
    label: t('nav.sections.essentials'),
    items: essentials,
    essential: true,
  });

  // Domain sections (each collapsible): concrete names, no "Geral"/"Trabalho".
  const pessoas: NavItem[] = [];
  if (has(PortalModule.Colaboradores)) pessoas.push(mod(PortalModule.Colaboradores, '/people', t));
  if (has(PortalModule.Rh)) pessoas.push(mod(PortalModule.Rh, '/rh', t));
  if (has(PortalModule.Presencas)) pessoas.push(mod(PortalModule.Presencas, '/presencas', t));
  if (pessoas.length)
    groups.push({ id: 'pessoas', label: t('nav.sections.pessoas'), items: pessoas });

  if (has(PortalModule.Onboarding) || has(PortalModule.Viaturas)) {
    const items: NavItem[] = [];
    if (has(PortalModule.Onboarding)) {
      items.push(
        { to: '/submissions', label: t('nav.registos'), icon: 'list' },
        { to: '/requests', label: t('nav.convites'), icon: 'mail' },
        { to: '/new', label: t('nav.novoRegisto'), icon: 'plus' },
      );
    }
    if (has(PortalModule.Viaturas)) {
      items.push(mod(PortalModule.Viaturas, '/viaturas', t));
    }
    groups.push({ id: 'onboarding', label: t('nav.sections.onboarding'), items });
  }
  if (has(PortalModule.Equipa) || has(PortalModule.Artigos)) {
    const items: NavItem[] = [];
    if (has(PortalModule.Equipa)) items.push(mod(PortalModule.Equipa, '/equipa', t));
    if (has(PortalModule.Artigos)) items.push(mod(PortalModule.Artigos, '/artigos', t));
    groups.push({ id: 'investigacao', label: t('nav.sections.investigacao'), items });
  }
  if (has(PortalModule.SafeLab)) {
    groups.push({
      id: 'laboratorio',
      label: t('nav.sections.laboratorio'),
      items: [mod(PortalModule.SafeLab, '/safelab', t)],
    });
  }

  const sistema: NavItem[] = [];
  if (has(PortalModule.Definicoes)) {
    sistema.push({
      to: '/settings',
      label: t(`modules.${MODULE_KEY[PortalModule.Definicoes]}`),
      icon: MODULE_ICONS[PortalModule.Definicoes],
      children: [
        { to: '/settings/global', label: t('nav.settings.global'), icon: 'cog' },
        { to: '/settings/campos', label: t('nav.settings.campos'), icon: 'list' },
        { to: '/settings/painel', label: t('nav.settings.painel'), icon: 'home' },
        { to: '/settings/integracao', label: t('nav.settings.integracao'), icon: 'spark' },
      ],
    });
  }
  if (has(PortalModule.Permissoes)) sistema.push(mod(PortalModule.Permissoes, '/permissions', t));
  if (has(PortalModule.ActiveDirectory)) sistema.push(mod(PortalModule.ActiveDirectory, '/ad', t));
  if (isAdminIt) sistema.push({ to: '/ti', label: t('nav.it'), icon: 'headset' });
  if (sistema.length > 0)
    groups.push({ id: 'sistema', label: t('nav.sections.sistema'), items: sistema });

  return groups;
}

export function UnifiedShell({ children }: { children: ReactNode }) {
  const { user, isManager, isResearcher, isAdminIt, logout } = useAuth();
  const { has, access } = useAccess();
  const { t, lang, toggle } = useI18n();
  const branding = useBranding();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [theme, toggleTheme] = useTheme();
  const [drawer, setDrawer] = useState(false);
  const [compact, setCompact] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);

  // ⌘K / Ctrl+K opens the command palette from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // Expansion is purely route-driven: by default only the section (and parent)
  // that owns the active route is open. A manual click records a TRANSIENT
  // override here, which is cleared on every navigation so sections open and
  // close on their own as the user moves between tabs. Nothing is persisted.
  const [override, setOverride] = useState<Record<string, boolean>>({});

  useEffect(() => setDrawer(false), [pathname]);
  useEffect(() => setOverride({}), [pathname]);

  const groups = buildGroups({ has, isManager, isResearcher, isAdminIt, t });

  // True when a section owns the active route (any item active, or any item's
  // child active). Drives the default open/closed state for the section.
  function sectionContainsActive(g: NavGroup): boolean {
    return g.items.some((it) => isBranchActive(pathname, it));
  }

  // A parent follows the route (open when a child is active) unless the user
  // has toggled it this navigation.
  function isOpen(item: NavItem): boolean {
    return override[item.to] ?? isBranchActive(pathname, item);
  }
  function toggleExpand(item: NavItem) {
    setOverride((o) => ({ ...o, [item.to]: !isOpen(item) }));
  }

  // A section follows the route (open when it owns the active route) unless the
  // user has toggled it this navigation.
  function isSecOpen(id: string, containsActive: boolean): boolean {
    return override[`sec:${id}`] ?? containsActive;
  }
  function toggleSec(id: string, containsActive: boolean) {
    const key = `sec:${id}`;
    setOverride((o) => ({ ...o, [key]: !(o[key] ?? containsActive) }));
  }

  // Flat nav model for the browser-style tab bar (labels for opened panels).
  // Parents with sub-options contribute their children, not themselves.
  const flatItems = groups.flatMap((g) =>
    g.items.flatMap((it) => (it.children ? it.children : [it])),
  );
  const tabNavItems = [
    { to: '/me', label: t('nav.meuPerfil') },
    ...flatItems.map((it) => ({ to: it.to, label: it.label })),
  ];

  // Command palette entries: every destination the user can reach, tagged with
  // its section (or parent) so results read clearly.
  const commands: Command[] = groups.flatMap((g) =>
    g.items.flatMap((it) =>
      it.children
        ? it.children.map((c) => ({
            to: c.to,
            label: c.label,
            section: it.label,
            icon: c.icon,
          }))
        : [{ to: it.to, label: it.label, section: g.label, icon: it.icon }],
    ),
  );
  const isDirection = user?.roles.includes(Role.Direction) ?? false;
  const role = access?.isAdmin
    ? t('roles.admin')
    : isManager
      ? t('roles.manager')
      : isDirection
        ? t('roles.direction')
        : isResearcher
          ? t('roles.researcher')
          : t('roles.collaborator');

  // Breadcrumb + page title for the header, from the active route. A sub-option
  // puts its parent in the breadcrumb (e.g. Definições › Campos da ficha).
  let crumb = '';
  let title = 'Portal';
  let found = false;
  for (const g of groups) {
    for (const it of g.items) {
      const child = it.children?.find((c) => isActive(pathname, c));
      if (child) {
        crumb = it.label;
        title = child.label;
        found = true;
        break;
      }
      if (isActive(pathname, it)) {
        crumb = g.label;
        title = it.label;
        found = true;
        break;
      }
    }
    if (found) break;
  }

  function go(to: string) {
    navigate(to);
  }

  // The header button collapses the sidebar to an icon rail on desktop, and
  // opens/closes the off-canvas drawer on phones/tablets.
  function onMenu() {
    const isDrawer =
      typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches;
    if (isDrawer) setDrawer((v) => !v);
    else setCompact((v) => !v);
  }

  return (
    <div className={`ush${drawer ? ' drawer-open' : ''}${compact ? ' erp-compact' : ''}`}>
      <div className="ush-scrim" onClick={() => setDrawer(false)} />

      <aside className="ush-sb erp-sb">
        <div className="erp-side">
          <div className="erp-brand">
            {branding?.icon ? (
              <img className="erp-brand-mark is-img" src={branding.icon} alt="" />
            ) : (
              <div className="erp-brand-mark">C</div>
            )}
            <div className="erp-brand-text">
              <div className="erp-brand-name">CIIMAR Portal</div>
              <div className="erp-brand-sub">{t('shell.brandSub')}</div>
            </div>
          </div>
          <button
            type="button"
            className="erp-cmdk-btn"
            onClick={() => setCmdOpen(true)}
            title={t('topbar.searchTitle')}
          >
            <Icon name="search" size={15} />
            <span className="erp-cmdk-text">{t('common.search')}…</span>
            <span className="erp-cmdk-kbd">
              <kbd>Ctrl</kbd>
              <kbd>K</kbd>
            </span>
          </button>
          <nav className="erp-nav">
            {groups.map((g) => {
              const containsActive = sectionContainsActive(g);
              // In the icon rail every item is shown (headers are hidden), so
              // sections are forced open there regardless of the route state.
              const secOpen = g.essential || compact || isSecOpen(g.id, containsActive);
              return (
                <div className={`erp-group${secOpen ? '' : ' collapsed'}`} key={g.id}>
                  {g.essential ? null : (
                    <button
                      type="button"
                      className="erp-group-head"
                      onClick={() => toggleSec(g.id, containsActive)}
                      aria-expanded={secOpen}
                    >
                      <span>{g.label}</span>
                      <Icon
                        name="chevD"
                        size={14}
                        className={'erp-group-chevron' + (secOpen ? ' open' : '')}
                      />
                    </button>
                  )}
                  {secOpen &&
                    g.items.map((it) =>
                      it.children ? (
                        <div key={it.to} className="erp-branch">
                          <button
                            type="button"
                            className={
                              'erp-item erp-parent' +
                              (isBranchActive(pathname, it) ? ' branch-active' : '')
                            }
                            onClick={() => toggleExpand(it)}
                            title={it.label}
                            aria-expanded={isOpen(it)}
                          >
                            <Icon name={it.icon} size={19} className="erp-item-ic" />
                            <span className="erp-item-label">{it.label}</span>
                            <Icon
                              name="chevD"
                              size={15}
                              className={'erp-chevron' + (isOpen(it) ? ' open' : '')}
                            />
                          </button>
                          {isOpen(it) && (
                            <div className="erp-subitems">
                              {it.children.map((c) => (
                                <button
                                  key={c.to}
                                  type="button"
                                  className={
                                    'erp-item erp-subitem' +
                                    (isActive(pathname, c) ? ' active' : '')
                                  }
                                  onClick={() => go(c.to)}
                                  title={c.label}
                                >
                                  <span className="erp-subitem-dot" aria-hidden="true" />
                                  <span className="erp-item-label">{c.label}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          {/* Icon-rail hover flyout: the parent's sub-options. */}
                          <div className="erp-flyout erp-flyout-group" aria-hidden="true">
                            <div className="erp-flyout-title">{it.label}</div>
                            {it.children.map((c) => (
                              <button
                                key={c.to}
                                type="button"
                                className={
                                  'erp-flyout-link' + (isActive(pathname, c) ? ' active' : '')
                                }
                                onClick={() => go(c.to)}
                              >
                                {c.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div key={it.to} className="erp-navitem">
                          <button
                            type="button"
                            className={'erp-item' + (isActive(pathname, it) ? ' active' : '')}
                            onClick={() => go(it.to)}
                            title={it.label}
                          >
                            <Icon name={it.icon} size={19} className="erp-item-ic" />
                            <span className="erp-item-label">{it.label}</span>
                          </button>
                          {/* Icon-rail hover flyout: the item's label. */}
                          <span className="erp-flyout erp-flyout-label" aria-hidden="true">
                            {it.label}
                          </span>
                        </div>
                      ),
                    )}
                </div>
              );
            })}
          </nav>
          <div className="erp-foot">
            <div className="erp-foot-id">
              <div className="erp-avatar">{initialsOf(user?.displayName)}</div>
              <div className="erp-foot-text">
                <div className="erp-who-name">{user?.displayName}</div>
                <div className="erp-who-role">{role}</div>
              </div>
            </div>
            <button
              type="button"
              className="erp-foot-logout"
              onClick={() => void logout()}
              title={t('topbar.logout')}
              aria-label={t('topbar.logout')}
            >
              <Icon name="arrowR" size={compact ? 19 : 17} />
              {!compact && <span className="erp-item-label">{t('topbar.logout')}</span>}
            </button>
          </div>
        </div>
      </aside>

      <div className="ush-col">
        <ImpersonationBanner />
        <header className="ush-top erp-top">
          <button
            className="erp-icon-btn"
            onClick={onMenu}
            title={t('topbar.menu')}
            aria-label={t('topbar.menu')}
          >
            <Icon name="list" size={20} />
          </button>
          <NotificationsBell />
          <div className="erp-crumbs">
            {crumb && <span className="erp-crumb">{crumb}</span>}
            <span className="erp-title">{title}</span>
          </div>
          <div className="erp-spacer" />
          <div className="erp-actions">
            <button
              className="erp-icon-btn"
              onClick={() => setCmdOpen(true)}
              title={t('topbar.searchTitle')}
              aria-label={t('common.search')}
            >
              <Icon name="search" size={19} />
            </button>
            <button
              className="erp-icon-btn erp-lang-btn"
              onClick={toggle}
              title={lang === 'pt' ? t('topbar.switchToEnglish') : t('topbar.switchToPortuguese')}
              aria-label={t('topbar.language')}
            >
              {lang.toUpperCase()}
            </button>
            <button
              className="erp-icon-btn"
              onClick={toggleTheme}
              title={theme === 'dark' ? t('topbar.lightMode') : t('topbar.darkMode')}
              aria-label={t('topbar.toggleTheme')}
            >
              <Icon name={theme === 'dark' ? 'spark' : 'moon'} size={19} />
            </button>
          </div>
        </header>
        <TabBar navItems={tabNavItems} />
        <main className="ush-main app-main">{children}</main>
      </div>

      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} commands={commands} />
    </div>
  );
}
